import { useVirtualizer } from "@tanstack/react-virtual";
import { type RefObject, useLayoutEffect, useRef, useState } from "react";
import { calendarDate } from "@/shared/utils/calendarDate";
import { cn } from "@/shared/utils/cn";
import type { AgendaDays } from "../utils/agendaDays";

interface AgendaWeekStripProps {
  days: AgendaDays;
  activeKey: string;
  todayKey: string;
  isClosedDay: (dateKey: string) => boolean;
  onDaySelect: (dateKey: string) => void;
}

export function AgendaWeekStrip(props: AgendaWeekStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={scrollRef}
      aria-label="Days, swipe for other weeks"
      role="group"
      className="relative mx-auto my-0.5 h-[54px] w-full max-w-[960px] shrink-0 snap-x snap-mandatory overflow-x-auto overscroll-x-contain [mask-image:linear-gradient(90deg,transparent,#000_12px,#000_calc(100%-12px),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {/* Remounted per width so every week keeps one exact size and the virtualizer never re-anchors the scroll. */}
      {width > 0 && <WeekTrack key={width} {...props} width={width} scrollRef={scrollRef} />}
    </div>
  );
}

function WeekTrack({ days, activeKey, todayKey, isClosedDay, onDaySelect, width, scrollRef }: AgendaWeekStripProps & {
  width: number;
  scrollRef: RefObject<HTMLDivElement | null>;
}) {
  const activeWeek = days.weekOfDay(days.dayIndexOf(activeKey) ?? 0);
  const virtualizer = useVirtualizer({
    horizontal: true,
    count: days.weekCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => width,
    initialOffset: () => activeWeek * width,
    overscan: 1,
  });

  // Only the active day moves the strip; swiping it never changes the selection.
  useLayoutEffect(() => {
    virtualizer.scrollToOffset(activeWeek * width);
  }, [virtualizer, width, activeWeek]);

  const format = (dateKey: string, options: Intl.DateTimeFormatOptions) => calendarDate(dateKey).toLocaleDateString(undefined, options);

  return (
    <div className="relative h-full" style={{ width: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((item) => (
        <div key={item.key} className="absolute top-0 grid h-full snap-start grid-cols-7 gap-1 px-3 sm:px-[30px]" style={{ left: item.start, width: item.size }}>
          {days.weekDayKeys(item.index).map((dateKey) => {
            const active = dateKey === activeKey;
            const today = dateKey === todayKey;
            return (
              <button
                key={dateKey}
                type="button"
                aria-pressed={active}
                aria-current={today ? "date" : undefined}
                aria-label={format(dateKey, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                onClick={() => onDaySelect(dateKey)}
                className={cn(
                  "relative flex flex-col items-center justify-center rounded-xl text-[10px] leading-tight text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active ? "bg-primary text-primary-foreground/80" : "hover:bg-muted",
                  !active && isClosedDay(dateKey) && "opacity-45",
                )}
              >
                <span aria-hidden="true">{format(dateKey, { weekday: "short" })}</span>
                <span aria-hidden="true" className={cn("text-[15px] font-bold tabular-nums", active ? "text-primary-foreground" : dateKey < todayKey ? "text-muted-foreground" : "text-foreground")}>
                  {format(dateKey, { day: "numeric" })}
                </span>
                {today && !active && <span aria-hidden="true" className="absolute bottom-1 size-1 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
